// Cache Manager for storing and retrieving application data
import { UserInfo } from '../typings/auth';
import logger from './logger';

// Cache keys constants
const CACHE_KEYS = {
  USER_INFO: 'cfp_user_info',
  ID_TOKEN: 'cfp_id_token',
  AUTH_STATE: 'cfp_auth_state',
  APP_CONFIG: 'cfp_app_config'
} as const;

// Cache configuration
interface CacheConfig {
  useLocalStorage: boolean;
  expirationTime?: number; // in milliseconds
}

// Default cache configuration
const DEFAULT_CACHE_CONFIG: CacheConfig = {
  useLocalStorage: true,
  expirationTime: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
};

// Cache entry with metadata
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expirationTime?: number;
}

/**
 * @brief Comprehensive cache manager for application data
 */
class CacheManager {
  /**
   * @brief Clear all cached application data
   */
  public clearCache(): void {
    if (this.m_config.useLocalStorage) {
      Object.values(CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    }
  }
  private m_config: CacheConfig;

  /**
   * @brief Constructor to initialize the cache manager
   * @param config Optional cache configuration
   */
  constructor(config: Partial<CacheConfig> = {}) {
    this.m_config = { ...DEFAULT_CACHE_CONFIG, ...config };
  }

  /**
   * @brief Store user information in cache
   * @param userInfo User information to cache
   */
  public cacheUserInfo(userInfo: UserInfo): void {
    try {
      const cacheEntry: CacheEntry<UserInfo> = {
        data: userInfo,
        timestamp: Date.now(),
        expirationTime: this.m_config.expirationTime
      };

      if (this.m_config.useLocalStorage) {
        localStorage.setItem(CACHE_KEYS.USER_INFO, JSON.stringify(cacheEntry));
      }

      // Dispatch a custom event so UI components can react immediately after fresh login
      try {
        const evt = new CustomEvent('cfp:userInfoCached', { detail: { userInfo } });
        window.dispatchEvent(evt);
        logger.debug('📢 CacheManager: Dispatched cfp:userInfoCached event');
      } catch (e) {
        logger.warn('⚠️ CacheManager: Failed to dispatch userInfoCached event');
      }

    } catch (error) {
      logger.error('❌ Failed to cache user info:', error);
    }
  }

  /**
   * @brief Retrieve user information from cache
   * @return Cached user info or null if not found/expired
   */
  public getCachedUserInfo(): UserInfo | null {
    try {
      // Try localStorage
      let cachedData: string | null = null;
      if (this.m_config.useLocalStorage) {
        cachedData = localStorage.getItem(CACHE_KEYS.USER_INFO);
      }

      if (!cachedData) {
        logger.debug('ℹ️ No cached user info found');
        return null;
      }

      const cacheEntry: CacheEntry<UserInfo> = JSON.parse(cachedData);
      
      // Check if cache entry has expired
      if (this.isCacheEntryExpired(cacheEntry)) {
        logger.debug('ℹ️ Cached user info has expired');
        this.clearUserInfo();
        return null;
      }

      logger.debug('✅ Cached user info retrieved successfully:', cacheEntry.data);
      return cacheEntry.data;
    } catch (error) {
      logger.error('❌ Failed to retrieve cached user info:', error);
      return null;
    }
  }

  /**
   * @brief Clear user information from cache
   */
  public clearUserInfo(): void {
    try {
      if (this.m_config.useLocalStorage) {
        localStorage.removeItem(CACHE_KEYS.USER_INFO);
      }

    } catch (error) {
      logger.error('❌ Failed to clear user info cache:', error);
    }
  }

  /**
   * @brief Store ID token in cache
   * @param token ID token to cache
   */
  public cacheIdToken(token: string): void {
    try {
      const cacheEntry: CacheEntry<string> = {
        data: token,
        timestamp: Date.now(),
        expirationTime: this.m_config.expirationTime
      };

      if (this.m_config.useLocalStorage) {
        localStorage.setItem(CACHE_KEYS.ID_TOKEN, JSON.stringify(cacheEntry));
      }

    } catch (error) {
      logger.error('❌ Failed to cache ID token:', error);
    }
  }

  /**
   * @brief Retrieve ID token from cache
   * @return Cached ID token or null if not found/expired
   */
  public getCachedIdToken(): string | null {
    try {
      // Try localStorage
      let cachedData: string | null = null;
      
      if (this.m_config.useLocalStorage) {
        cachedData = localStorage.getItem(CACHE_KEYS.ID_TOKEN);
      }

      if (!cachedData) {
        logger.debug('ℹ️ No cached ID token found');
        return null;
      }

      const cacheEntry: CacheEntry<string> = JSON.parse(cachedData);
      
      // Check if cache entry has expired
      if (this.isCacheEntryExpired(cacheEntry)) {
        logger.debug('ℹ️ Cached ID token has expired');
        this.clearIdToken();
        return null;
      }

      return cacheEntry.data;
    } catch (error) {
      logger.error('❌ Failed to retrieve cached ID token:', error);
      return null;
    }
  }

  /**
   * @brief Clear ID token from cache
   */
  public clearIdToken(): void {
    try {
      if (this.m_config.useLocalStorage) {
        localStorage.removeItem(CACHE_KEYS.ID_TOKEN);
      }

      logger.debug('✅ ID token cache cleared');
    } catch (error) {
      logger.error('❌ Failed to clear ID token cache:', error);
    }
  }

  /**
   * @brief Store generic data in cache
   * @param key Cache key
   * @param data Data to cache
   * @param expirationTime Optional expiration time in milliseconds
   */
  public cacheData<T>(key: string, data: T, expirationTime?: number): void {
    try {
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        expirationTime: expirationTime || this.m_config.expirationTime
      };

      if (this.m_config.useLocalStorage) {
        localStorage.setItem(key, JSON.stringify(cacheEntry));
      }

      logger.debug(`✅ Data cached successfully for key: ${key}`);
    } catch (error) {
      logger.error(`❌ Failed to cache data for key ${key}:`, error);
    }
  }

  /**
   * @brief Retrieve generic data from cache
   * @param key Cache key
   * @return Cached data or null if not found/expired
   */
  public getCachedData<T>(key: string): T | null {
    try {
      // Try localStorage
      let cachedData: string | null = null;

      if (this.m_config.useLocalStorage) {
        cachedData = localStorage.getItem(key);
      }

      if (!cachedData) {
        logger.debug(`ℹ️ No cached data found for key: ${key}`);
        return null;
      }

      const cacheEntry: CacheEntry<T> = JSON.parse(cachedData);
      
      // Check if cache entry has expired
      if (this.isCacheEntryExpired(cacheEntry)) {
        logger.debug(`ℹ️ Cached data has expired for key: ${key}`);
        this.clearData(key);
        return null;
      }

      logger.debug(`✅ Cached data retrieved successfully for key: ${key}`);
      return cacheEntry.data;
    } catch (error) {
      logger.error(`❌ Failed to retrieve cached data for key ${key}:`, error);
      return null;
    }
  }

  /**
   * @brief Clear generic data from cache
   * @param key Cache key
   */
  public clearData(key: string): void {
    try {
      if (this.m_config.useLocalStorage) {
        localStorage.removeItem(key);
      }

      logger.debug(`✅ Cache cleared for key: ${key}`);
    } catch (error) {
      logger.error(`❌ Failed to clear cache for key ${key}:`, error);
    }
  }

  /**
   * @brief Clear all application cache
   */
  public clearAllCache(): void {
    try {
      const keysToRemove = Object.values(CACHE_KEYS);
      
      keysToRemove.forEach(key => {
        if (this.m_config.useLocalStorage) {
          localStorage.removeItem(key);
        }
      });

      logger.info('✅ All application cache cleared');
    } catch (error) {
      logger.error('❌ Failed to clear all cache:', error);
    }
  }

  /**
   * @brief Check if cache entry has expired
   * @param cacheEntry Cache entry to check
   * @return True if expired, false otherwise
   */
  private isCacheEntryExpired(cacheEntry: CacheEntry<any>): boolean {
    if (!cacheEntry.expirationTime) {
      return false; // No expiration time set
    }

    const now = Date.now();
    const expirationTimestamp = cacheEntry.timestamp + cacheEntry.expirationTime;
    
    return now > expirationTimestamp;
  }

  /**
   * @brief Get cache statistics
   * @return Object containing cache statistics
   */
  public getCacheStats(): { totalEntries: number; expiredEntries: number; validEntries: number } {
    try {
      let totalEntries = 0;
      let expiredEntries = 0;
      let validEntries = 0;

      const keysToCheck = Object.values(CACHE_KEYS);
      
      keysToCheck.forEach(key => {
        let cachedData: string | null = null;
        
        if (this.m_config.useLocalStorage) {
          cachedData = localStorage.getItem(key);
        }

        if (cachedData) {
          totalEntries++;
          try {
            const cacheEntry: CacheEntry<any> = JSON.parse(cachedData);
            if (this.isCacheEntryExpired(cacheEntry)) {
              expiredEntries++;
            } else {
              validEntries++;
            }
          } catch {
            // Invalid cache entry, count as expired
            expiredEntries++;
          }
        }
      });

      return { totalEntries, expiredEntries, validEntries };
    } catch (error) {
      logger.error('❌ Failed to get cache statistics:', error);
      return { totalEntries: 0, expiredEntries: 0, validEntries: 0 };
    }
  }

  /**
   * @brief Update cache configuration
   * @param config New cache configuration
   */
  public updateConfig(config: Partial<CacheConfig>): void {
    this.m_config = { ...this.m_config, ...config };
    logger.debug('✅ Cache configuration updated:', this.m_config);
  }

  /**
   * @brief Get current cache configuration
   * @return Current cache configuration
   */
  public getConfig(): CacheConfig {
    return { ...this.m_config };
  }
}

// Export both the class and a singleton instance
export { CacheManager };
export const cacheManager = new CacheManager();
